import { useState, useRef, useCallback, useEffect } from 'react';
import { resolveAudioUrl } from '../../user_guides/audio/audioController';
import { ensureCdnCookies } from '../../user_guides/cdnAuth';
import { KB_PACKAGE_KEY } from '../../content/kbClient.js';

/**
 * Single-stream per-section narration for the Knowledge Base (#1088).
 *
 * Returns the id of the section currently playing (or null) and a `toggle` that
 * plays a section's narration, or stops it when that section is already the one
 * playing - so only the active section shows its stop control. Starting a new
 * section stops the previous one; playback auto-resets to idle on end / error.
 *
 * Reuses the guide audioController's resolveAudioUrl: a KB section's `audio`
 * map shares the `{ female, male }` shape of a guide step's, and the audio lives
 * under the same CDN packageKey/audio/<locale>/ layout.
 *
 * @param {{locale: string, voiceGender: string}} opts
 */
export const useKbNarration = ({ locale, voiceGender }) => {
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const teardown = () => {
    const a = audioRef.current;
    if (a) {
      a.onended = null;
      a.pause();
      a.src = '';
      audioRef.current = null;
    }
  };

  const stop = useCallback(() => {
    teardown();
    setPlayingId(null);
  }, []);

  const toggle = useCallback(
    (section) => {
      if (playingId === section.id) {
        stop();
        return;
      }
      const url = resolveAudioUrl(section, { voiceGender, packageKey: KB_PACKAGE_KEY, locale });
      if (!url) return;
      // Fire-and-forget cookie freshness (#1566). NEVER await here: awaiting
      // would burn the click's user activation and autoplay-block narration.
      ensureCdnCookies();
      teardown();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlayingId((cur) => (cur === section.id ? null : cur));
      setPlayingId(section.id);
      const started = audio.play();
      if (started && typeof started.catch === 'function') {
        started.catch((err) => {
          // Autoplay-gesture or media errors must not wedge the UI in "playing".
          console.warn('KB narration play() failed:', err && err.name);
          setPlayingId((cur) => (cur === section.id ? null : cur));
        });
      }
    },
    [playingId, stop, voiceGender, locale],
  );

  // Stop on unmount and whenever voice/locale change (the cached audio is stale).
  useEffect(() => stop, [stop, voiceGender, locale]);

  return { playingId, toggle, stop };
};

export default useKbNarration;
